import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon, TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import httpRequest from "../utils/httpRequest";

const LastCreditTransactionsModal = ({ open, onClose, selectedFY }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page,
        limit: pageSize,
        fy: selectedFY || "all",
      });

      const response = await httpRequest(`customers/last-credit-transactions?${query}`, "GET");
      setTransactions(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Failed to fetch credit transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTransactions();
    }
  }, [open, page, pageSize, selectedFY]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: "60vh",
          maxHeight: "85vh",
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'primary.main',
        color: 'white'
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <TrendingUpIcon sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Credit Transactions
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, pb: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            <TableContainer 
              component={Paper} 
              sx={{ 
                mt: 1,
                borderRadius: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                maxHeight: '400px',
                overflow: 'auto',
                '& .MuiTable-root': {
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: 'grey.50',
                    '& th': {
                      borderBottom: '2px solid',
                      borderColor: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      py: 1.5,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: 'grey.50',
                    }
                  }}>
                    <TableCell>Customer</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Payment Mode</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction, index) => (
                      <TableRow 
                        key={transaction.id} 
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'primary.50' },
                          '& td': {
                            py: 1.5,
                            fontSize: '0.875rem',
                            borderBottom: '1px solid',
                            borderColor: 'grey.200',
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {transaction.customer?.name || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.customer?.account_number || "N/A"} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 24 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'success.main', 
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }}
                          >
                            {formatAmount(transaction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {transaction.description || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.payment_mode || "N/A"} 
                            size="small" 
                            color="primary"
                            variant="filled"
                            sx={{ fontSize: '0.75rem', height: 24 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {formatDate(transaction.transaction_date)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No credit transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination.totalPages > 1 && (
              <Box 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center" 
                mt={2}
                px={1}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" color="text.secondary">
                    {pagination.totalItems} transactions
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 70 }}>
                    <Select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      sx={{ height: 32, fontSize: '0.75rem' }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Pagination
                  count={pagination.totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  shape="rounded"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '0.875rem',
                      minWidth: 32,
                      height: 32,
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LastCreditTransactionsModal; 